import rest from './rest'
import { cognitoConfig } from './aws-config'
import { Auth } from 'aws-amplify'
import { MediaCdn, mediaCdn } from '@/lib/media-cdn'
import { inboxMediaUrl } from '@/lib/inbox-media'
import configAxios from './rest/axios.config'

const useProdLoginForDev = false

export const basePaths = {
  analysis: '/analysis',
  dashboard: '/analysis',
  clientAdmin: '/client-admin',
  moments: '/moments',
  conversations: '/conversations',
  commentary: '/commentary',
  ingestion: '/ingestion',
  transcription: '/transcription',
  transcriptionManager:
    'https://transcription-api.data.test.kaiconversations.ai'
}

export interface Config {
  env: 'dev' | 'test' | 'root' | 'live'
  basePath: string
  apis: Record<string, unknown>
  mediaCdn: MediaCdn
  inboxMediaUrl(path: string): string
}

const config: Record<string, () => Config> = {
  localstack: () => {
    Auth.configure(cognitoConfig)
    const client = configAxios()
    const apis = rest(client, {
      analysis: 'http://analysis-reporting:8081/dev',
      dashboard: 'http://analysis-reporting:8081/dev',
      clientAdmin: 'http://client-admin:8280/dev',
      moments: 'http://analysis-moments:8085/dev',
      conversations: 'http://conversations:8086/dev',
      commentary: 'http://commentary:8085/dev',
      ingestion: 'http://analysis-ingestion:4569',
      transcription: 'http://transcription:8086/dev',
      transcriptionManager: 'http://host.docker.internal:3000'
    })
    return {
      env: 'dev',
      basePath: 'http://localstack:4566/',
      apis,
      mediaCdn: mediaCdn({
        host: 'http://localstack:4566/',
        conversationsApi: apis.conversations
      }),
      inboxMediaUrl: inboxMediaUrl({
        host: 'http://localstack:4566/'
      })
    }
  },
  development: () => {
    Auth.configure(cognitoConfig)
    const client = configAxios('https://api.local.kaiconversations.ai')
    const apis = rest(client, basePaths)
    return {
      env: 'dev',
      basePath: 'https://api.local.kaiconversations.ai',
      apis,
      mediaCdn: mediaCdn({
        host: 'https://media.local.kaiconversations.ai',
        conversationsApi: apis.conversations
      }),
      inboxMediaUrl: inboxMediaUrl({
        host: 'https://inbox.local.kaiconversations.ai/'
      })
    }
  },

  test: () => {
    Auth.configure(cognitoConfig)
    const client = configAxios('https://api.test.kaiconversations.ai')
    const apis = rest(client, basePaths)
    return {
      env: 'test',
      basePath: 'https://api.test.kaiconversations.ai',
      apis,
      mediaCdn: mediaCdn({
        host: 'https://media.test.kaiconversations.ai',
        conversationsApi: apis.conversations
      }),
      inboxMediaUrl: inboxMediaUrl({
        host: 'https://inbox.test.kaiconversations.ai/'
      })
    }
  },
  uat: () => {
    Auth.configure(cognitoConfig)
    const client = configAxios('https://api.test.kaiconversations.ai')
    const apis = rest(client, basePaths)
    return {
      env: 'test',
      basePath: 'https://api.test.kaiconversations.ai',
      apis,
      mediaCdn: mediaCdn({
        host: 'https://media.test.kaiconversations.ai',
        conversationsApi: apis.conversations
      }),
      inboxMediaUrl: inboxMediaUrl({
        host: 'https://inbox.test.kaiconversations.ai/'
      })
    }
  },
  dev: () => {
    Auth.configure(cognitoConfig)
    const client = configAxios('https://api.test.kaiconversations.ai')
    const apis = rest(client, basePaths)
    return {
      env: 'dev',
      basePath: 'https://api.test.kaiconversations.ai',
      apis,
      mediaCdn: mediaCdn({
        host: 'https://media.test.kaiconversations.ai',
        conversationsApi: apis.conversations
      }),
      inboxMediaUrl: inboxMediaUrl({
        host: 'https://inbox.test.kaiconversations.ai/'
      })
    }
  },
  testlocal: () => {
    Auth.configure(cognitoConfig)
    const client = configAxios('https://api.test.kaiconversations.ai')
    const apis = rest(client, basePaths)
    return {
      env: 'test',
      basePath: 'https://api.test.kaiconversations.ai',
      apis,
      mediaCdn: mediaCdn({
        host: 'https://media.test.kaiconversations.ai',
        conversationsApi: apis.conversations
      }),
      inboxMediaUrl: inboxMediaUrl({
        host: 'https://inbox.test.kaiconversations.ai/'
      })
    }
  },
  livelocal: () => {
    Auth.configure(cognitoConfig)
    const client = configAxios('https://api.kaiconversations.ai')
    const apis = rest(client, {
      ...basePaths,
      transcriptionManager:
        'https://transcription-api.data.live.kaiconversations.ai'
    })
    return {
      env: 'live',
      basePath: 'https://api.kaiconversations.ai',
      apis,
      mediaCdn: mediaCdn({
        host: 'https://media.kaiconversations.ai',
        conversationsApi: apis.conversations
      }),
      inboxMediaUrl: inboxMediaUrl({
        host: 'https://inbox.kaiconversations.ai/'
      })
    }
  },

  production: () => {
    Auth.configure(cognitoConfig)
    const client = configAxios('https://api.root.kaiconversations.ai')
    const apis = rest(client, {
      ...basePaths,
      transcriptionManager: 'https://transcription-api.data.kaiconversations.ai'
    })
    return {
      env: 'root',
      basePath: 'https://api.root.kaiconversations.ai',
      apis,
      mediaCdn: mediaCdn({
        host: 'https://media.root.kaiconversations.ai',
        conversationsApi: apis.conversations
      }),
      inboxMediaUrl: inboxMediaUrl({
        host: 'https://inbox.root.kaiconversations.ai/'
      })
    }
  },

  live: () => {
    Auth.configure(cognitoConfig)
    const client = configAxios('https://api.kaiconversations.ai')
    const apis = rest(client, {
      ...basePaths,
      transcriptionManager:
        'https://transcription-api.data.live.kaiconversations.ai'
    })
    return {
      env: 'live',
      basePath: 'https://api.kaiconversations.ai',
      apis,
      mediaCdn: mediaCdn({
        host: 'https://media.kaiconversations.ai',
        conversationsApi: apis.conversations
      }),
      inboxMediaUrl: inboxMediaUrl({
        host: 'https://inbox.kaiconversations.ai/'
      })
    }
  }
}

export default function(env: string) {
  //eslint-disable-next-line
  console.log(`ENV: '${env}'`)
  return config[
    env === 'development' && useProdLoginForDev ? 'production' : env
  ]()
}
